/**
 * TrialStatusBar - Displays trial status card (matches schedule page pattern).
 *
 * Two states:
 *  - Lessons remaining → yellow card with count + days left
 *  - Limit reached     → red card with CTA to settings
 *
 * Styling mirrors schedule/index.tsx trial banners (rounded-xl card, /10 bg, /20 border).
 */

import React from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/lib/i18n';
import type { ParentStatus } from '@/hooks/useParentStatus';

interface TrialStatusBarProps {
  status: ParentStatus;
  childName?: string;
}

export const TrialStatusBar: React.FC<TrialStatusBarProps> = ({ status }) => {
  const { t } = useTranslation();

  if (!status.isInTrial) return null;

  const {
    trialDaysRemaining,
    trialLessonsRemaining,
    trialLessonsMax,
    canScheduleLesson,
  } = status;

  if (!canScheduleLesson) {
    return (
      <div className="mb-6 p-4 rounded-xl bg-[#FF453A]/10 border border-[#FF453A]/20 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-[#FF453A]">{t('Trial lesson limit reached')}</p>
          <p className="text-sm text-[#636366] mt-1">
            {t("You've used all")} {trialLessonsMax} {t('trial lessons. Subscribe to continue scheduling lessons with Turn.')}
          </p>
          <Link
            to="/settings"
            className="inline-block mt-2 text-sm text-[#0A84FF] hover:underline font-medium"
          >
            {t('Manage Subscription')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 p-4 rounded-xl bg-[#FFD60A]/10 border border-[#FFD60A]/20 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Calendar className="w-5 h-5 text-[#FFD60A]" />
        <span className="text-sm text-[#3C3C43]">
          <strong className="text-[#FFD60A]">{trialLessonsRemaining}</strong> {t('of')} {trialLessonsMax} {t('trial lessons remaining')}
        </span>
      </div>
      {trialDaysRemaining !== null && (
        <span className="text-xs text-[#AEAEB2]">
          {trialDaysRemaining} {t('days left in trial')}
        </span>
      )}
    </div>
  );
};

export default TrialStatusBar;
